/**
 * What an article is for, and therefore where it surfaces: a `brand_details`
 * article is the one a brand's own page shows, a `general` article surfaces only
 * in the articles list.
 *
 * Modeled as an enum column rather than a classification axis because it routes
 * a surface rather than describing the article, and a two-value routing
 * distinction is better answered by a column comparison the compiler checks than
 * by a registry row a query has to hope exists. Candidate to migrate to an
 * `article_type` axis once the set of types stabilizes and grows, the same note
 * `MessageType` already carries.
 *
 * The service does not enforce placement: it exposes the type as a filterable
 * column and each surface asks for the types it wants.
 */
export enum ArticleType {
	GENERAL = 'general',
	BRAND_DETAILS = 'brand_details',
}

/**
 * Article lifecycle states.
 *
 * `published` is reached only through the transition endpoint and carries a
 * stricter role gate than authoring, so publication is a distinct act rather
 * than a field an author can set.
 */
export enum ArticleStatus {
	DRAFT = 'draft',
	IN_REVIEW = 'in_review',
	PUBLISHED = 'published',
	ARCHIVED = 'archived',
}

/**
 * State of an in-flight AI translation job for an article row — orthogonal to
 * `ArticleStatus` (publication lifecycle). Null means the row was never
 * machine-translated, which is not the same as a job that has not started.
 *
 * Written only by the translation flow: the request marks a target `pending`,
 * the worker marks it `translating` and then `ready` or `failed`. It is
 * unreachable through the article create and update schemas.
 */
export enum ArticleTranslationStatus {
	PENDING = 'pending',
	TRANSLATING = 'translating',
	READY = 'ready',
	FAILED = 'failed',
}
